import { ProcessGeneral } from 'api/endpoints/processes';

import BaseRepo from '../../../BaseRepo';
import { buildBadRequestApiError } from '../../../helpers/errors';
import { ApiResponseAsync } from '../../../models';
import { PatchTyped } from '../../../patch';
import { SearchRequestTyped } from '../../../search';
import {
  BarcodeImporterScenarioWithHypermedia,
  CreateBarcodeImporterScenarioRequest,
  IBarcodeImporterScenarioRepo,
  SearcheableBarcodeImporterScenario,
} from './IBarcodeImporterScenarioRepo';

export function hasBarcodeImporterLicense(draft?: ProcessGeneral) {
  return !!draft?._links?.['searchBarcodeImporterScenarios'];
}

export class BarcodeImporterScenarioRepo
  extends BaseRepo
  implements IBarcodeImporterScenarioRepo
{
  search = (
    draft: ProcessGeneral,
    request: SearchRequestTyped<SearcheableBarcodeImporterScenario>
  ): ApiResponseAsync<BarcodeImporterScenarioWithHypermedia[]> => {
    const link = draft._links
      ? draft._links['searchBarcodeImporterScenarios']
      : undefined;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "searchBarcodeImporterScenarios" is not available in process draft'
          )
        )
      );
    }

    return this.helpers.executeSearch(link, request);
  };

  create = (
    draft: ProcessGeneral,
    request: CreateBarcodeImporterScenarioRequest
  ): ApiResponseAsync<BarcodeImporterScenarioWithHypermedia> => {
    const link = draft._links?.['createBarcodeImporterScenario'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "createBarcodeImporterScenario" is not available in process draft'
          )
        )
      );
    }

    return this.helpers.handlePost(link.href, request);
  };

  update = (
    scenario: BarcodeImporterScenarioWithHypermedia,
    patch: PatchTyped<CreateBarcodeImporterScenarioRequest>
  ): ApiResponseAsync<BarcodeImporterScenarioWithHypermedia> => {
    const link = scenario._links ? scenario._links['update'] : undefined;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "update" is not available in Barcode Import scenario')
        )
      );
    }

    return this.helpers.executePatch(link, patch, true);
  };

  activate = (
    scenario: BarcodeImporterScenarioWithHypermedia
  ): ApiResponseAsync<BarcodeImporterScenarioWithHypermedia> => {
    const link = scenario._links ? scenario._links['activate'] : undefined;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "activate" is not available in Barcode Import scenario'
          )
        )
      );
    }

    return this.helpers.handlePost(link.href, {});
  };

  deactivate = (
    scenario: BarcodeImporterScenarioWithHypermedia
  ): ApiResponseAsync<BarcodeImporterScenarioWithHypermedia> => {
    const link = scenario._links ? scenario._links['deactivate'] : undefined;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "deactivate" is not available in Barcode Import scenario'
          )
        )
      );
    }

    return this.helpers.handlePost(link.href, {});
  };

  delete = (
    scenario: BarcodeImporterScenarioWithHypermedia
  ): ApiResponseAsync<void> => {
    const link = scenario._links ? scenario._links['delete'] : undefined;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "delete" is not available in Barcode Import scenario')
        )
      );
    }

    return this.helpers.handleDelete(link.href);
  };
}
